import {CircularProgressBar} from "@/components/circularProgressBar/CircularProgressBar.tsx";
import {MoreList} from "@/components/moreList/MoreList.tsx";
import {ColumnSpec} from "@/components/table/Table.types.ts";
import {CHECK_LABEL_MAX_LENGTH} from "@/lib/displayUtils.ts";
import {percentage, shortDateUser} from "@/lib/format.tsx";
import {AccountUser} from "@/lib/types/accounts.ts";
import {CheckTypeDisplay, Service} from "@/lib/types/checks.ts";
import {
  findIntervalSuffix,
  intervalOptionByMinute,
  intervalValueDisplay,
} from "@/pages/checks/components/form/fields/interval/utils.ts";
import {CheckActions} from "@/pages/checks/list/actions/CheckActions.tsx";
import {CheckName} from "@/pages/checks/list/components/CheckName.tsx";
import {CheckResponseTime} from "@/pages/checks/list/components/CheckResponseTime.tsx";
import {CheckStatusChip} from "@/pages/checks/list/components/CheckStatusChip.tsx";
import {ChecksTags} from "@/pages/checks/list/components/CheckTags.tsx";
import {ViewSelector} from "@/pages/checks/list/components/ViewSelector/ViewSelector.tsx";
import {isEmptyGroupCheck} from "@/pages/checks/utils.ts";

export const DEFAULT_COLUMNS = ["name", "status", "type", "uptime_24h", "response", "tags"];

export const COLUMN_SPEC_FACTORIES: Record<string, (user: AccountUser) => ColumnSpec<Service>> = {
  name: () => ({
    fieldName: "name_lower",
    fieldTitle: "Name",
    enableSorting: true,
    meta: {headerClassName: "w-35"},
    accessor: (obj) => (
      <CheckName
        type={obj.monitoring_service_type}
        id={obj.id}
        name={obj.name}
        address={obj.msp_address}
        maxLength={CHECK_LABEL_MAX_LENGTH}
        isEmptyGroup={isEmptyGroupCheck(obj)}
      />
    ),
  }),
  status: () => ({
    fieldName: "status_sortable",
    fieldTitle: "Status",
    enableSorting: true,
    meta: {headerClassName: "w-10"},
    accessor: (obj) => <CheckStatusChip service={obj} />,
  }),
  type: () => ({
    fieldName: "monitoring_service_type",
    fieldTitle: "Type",
    enableSorting: true,
    meta: {headerClassName: "w-15"},
    accessor: (obj) => CheckTypeDisplay[obj.monitoring_service_type],
  }),
  uptime_24h: () => ({
    fieldName: "uptime",
    fieldTitle: "Uptime (24h)",
    meta: {headerClassName: "w-10", columnClassName: "text-nowrap"},
    accessor: (obj) =>
      obj.uptime24h !== null ? (
        <div className="align-items-center">
          <CircularProgressBar value={obj.uptime24h} variant="half" />
          <span className="mx-2">{percentage(obj.uptime24h, 2)}</span>
        </div>
      ) : (
        ""
      ),
  }),
  response: () => ({
    fieldName: "cached_response_time",
    fieldTitle: "Response",
    enableSorting: true,
    meta: {headerClassName: "w-15"},
    accessor: (obj) => <CheckResponseTime service={obj} />,
  }),
  tags: () => ({
    fieldName: "tags",
    fieldTitle: "Tags",
    meta: {headerClassName: "w-10"},
    accessor: (obj) => <ChecksTags tags={obj.tags} />,
  }),
  address: () => ({
    fieldName: "device__address",
    fieldTitle: "Address / URL",
    enableSorting: true,
    accessor: (obj) => obj.msp_address || "—",
  }),
  msp_interval: () => ({
    fieldName: "msp_interval",
    fieldTitle: "Interval",
    enableSorting: true,
    accessor: (obj) => {
      if (!obj.msp_interval) return "—";
      const option = intervalOptionByMinute(obj.msp_interval);
      return findIntervalSuffix(intervalValueDisplay(obj.msp_interval, option), option, true);
    },
  }),
  msp_uptime_sla: () => ({
    fieldName: "msp_uptime_sla",
    fieldTitle: "Uptime SLA",
    enableSorting: true,
    accessor: (obj) => (obj.msp_uptime_sla ? `${obj.msp_uptime_sla}%` : "—"),
  }),
  msp_response_time_sla: () => ({
    fieldName: "msp_response_time_sla",
    fieldTitle: "Response SLA",
    enableSorting: true,
    accessor: (obj) => (obj.msp_response_time_sla ? `${obj.msp_response_time_sla}ms` : "—"),
  }),
  contact_groups: () => ({
    fieldName: "contact_groups",
    fieldTitle: "Contact Groups",
    accessor: (obj) => (
      <MoreList entries={obj.contact_groups ?? []} showEntries={2} renderer={(cg) => <span>{cg.name}</span>} />
    ),
  }),
  escalations: () => ({
    fieldName: "escalations",
    fieldTitle: "Escalations",
    accessor: (obj) => (obj.escalations?.length ? `${obj.escalations.length} level(s)` : "—"),
  }),
  locations: () => ({
    fieldName: "locations",
    fieldTitle: "Locations",
    accessor: (obj) => (
      <MoreList entries={obj.monitoring_groups ?? []} showEntries={2} renderer={(mg) => <span>{mg.location}</span>} />
    ),
  }),
  created_at: (user) => ({
    fieldName: "created_at",
    fieldTitle: "Created At",
    enableSorting: true,
    accessor: (obj) => (obj.created_at ? shortDateUser(String(obj.created_at), user) : "—"),
  }),
  cached_last_down_alert_at: (user) => ({
    fieldName: "cached_last_down_alert_at",
    fieldTitle: "Last Down",
    enableSorting: true,
    accessor: (obj) =>
      obj.cached_last_down_alert_at ? shortDateUser(String(obj.cached_last_down_alert_at), user) : "—",
  }),
  cached_last_up_alert_at: (user) => ({
    fieldName: "cached_last_up_alert_at",
    fieldTitle: "Last Up",
    enableSorting: true,
    accessor: (obj) => (obj.cached_last_up_alert_at ? shortDateUser(String(obj.cached_last_up_alert_at), user) : "—"),
  }),
  msp_port: () => ({
    fieldName: "msp_port",
    fieldTitle: "Port",
    enableSorting: true,
    accessor: (obj) => (obj.msp_port != null ? String(obj.msp_port) : "—"),
  }),
  msp_encryption: () => ({
    fieldName: "msp_encryption",
    fieldTitle: "Encryption",
    enableSorting: true,
    accessor: (obj) => (obj.msp_encryption != null && obj.msp_encryption !== "" ? obj.msp_encryption : "—"),
  }),
  msp_dns_server: () => ({
    fieldName: "msp_dns_server",
    fieldTitle: "DNS Server",
    accessor: (obj) => (obj.msp_dns_server != null && obj.msp_dns_server !== "" ? obj.msp_dns_server : "—"),
  }),
  msp_dns_record_type: () => ({
    fieldName: "msp_dns_record_type",
    fieldTitle: "DNS Record Type",
    enableSorting: true,
    accessor: (obj) =>
      obj.msp_dns_record_type != null && obj.msp_dns_record_type !== "" ? obj.msp_dns_record_type : "—",
  }),
  msp_expect_string: () => ({
    fieldName: "msp_expect_string",
    fieldTitle: "Expect String",
    accessor: (obj) => (obj.msp_expect_string != null && obj.msp_expect_string !== "" ? obj.msp_expect_string : "—"),
  }),
  msp_send_string: () => ({
    fieldName: "msp_send_string",
    fieldTitle: "Send String",
    accessor: (obj) => (obj.msp_send_string != null && obj.msp_send_string !== "" ? obj.msp_send_string : "—"),
  }),
  msp_status_code: () => ({
    fieldName: "msp_status_code",
    fieldTitle: "Status Code",
    accessor: (obj) => (obj.msp_status_code != null && obj.msp_status_code !== "" ? obj.msp_status_code : "—"),
  }),
  msp_sensitivity: () => ({
    fieldName: "msp_sensitivity",
    fieldTitle: "Sensitivity",
    enableSorting: true,
    accessor: (obj) => (obj.msp_sensitivity != null && obj.msp_sensitivity !== "" ? obj.msp_sensitivity : "—"),
  }),
  msp_num_retries: () => ({
    fieldName: "msp_num_retries",
    fieldTitle: "Retries",
    enableSorting: true,
    // TODO: add msp_num_retries to Service as string and fix CreateCheckFormValues to not
    // inherit it from Service (decouple form types from GraphQL response types). Until then,
    // this cast is needed because GraphQL returns String! but Service omits the field to avoid
    // a type conflict with CreateCheckFormValues which types it as number.
    accessor: (obj) => {
      const val = (obj as unknown as Record<string, string>).msp_num_retries;
      return val != null && val !== "" ? val : "—";
    },
  }),
  msp_threshold: () => ({
    fieldName: "msp_threshold",
    fieldTitle: "Threshold",
    enableSorting: true,
    accessor: (obj) => (obj.msp_threshold != null ? `${obj.msp_threshold}ms` : "—"),
  }),
  msp_headers: () => ({
    fieldName: "msp_headers",
    fieldTitle: "Headers",
    accessor: (obj) => (obj.msp_headers ? "Configured" : "—"),
  }),
  msp_use_ip_version: () => ({
    fieldName: "msp_use_ip_version",
    fieldTitle: "IP Version",
    enableSorting: true,
    accessor: (obj) =>
      obj.msp_use_ip_version != null && obj.msp_use_ip_version !== "" ? obj.msp_use_ip_version : "Auto",
  }),
  msp_username: () => ({
    fieldName: "msp_username",
    fieldTitle: "Username",
    accessor: (obj) => obj.msp_username || "—",
  }),
  msp_notes: () => ({
    fieldName: "msp_notes",
    fieldTitle: "Notes",
    accessor: (obj) => (obj.msp_notes ? "Yes" : "—"),
  }),
  msp_include_in_global_metrics: () => ({
    fieldName: "msp_include_in_global_metrics",
    fieldTitle: "Global Metrics",
    enableSorting: true,
    accessor: (obj) => (obj.msp_include_in_global_metrics ? "Yes" : "No"),
  }),
};

export const buildQuickActionsSpec = (user: AccountUser): ColumnSpec<Service> => ({
  fieldName: "quick_actions",
  fieldTitle: <ViewSelector className="d-flex justify-content-end" />,
  meta: {headerClassName: "w-5", columnClassName: "cursor-default", allowPointerEvents: true},
  accessor: (obj) => (
    <CheckActions
      id={obj.id}
      name={obj.name}
      serviceType={obj.monitoring_service_type}
      isPaused={obj.is_paused}
      isUp={obj.cached_state_is_up}
      hasWriteAccess={user.has_write_access}
      hasAdminAccess={user.has_admin_access}
    />
  ),
});

export const buildColumnSpecs = (
  columnIds: string[],
  user: AccountUser,
  onRowClick?: (service: Service) => void
): ColumnSpec<Service>[] => {
  const cellOnClick: NonNullable<ColumnSpec<Service>["meta"]>["onClick"] = onRowClick
    ? (_event, cell) => {
        if (cell) onRowClick(cell.row.original);
      }
    : undefined;
  const specs: ColumnSpec<Service>[] = [];
  for (const id of columnIds) {
    const factory = COLUMN_SPEC_FACTORIES[id];
    if (factory) {
      const spec = factory(user);
      if (cellOnClick) {
        spec.meta = {...spec.meta, onClick: cellOnClick};
      }
      specs.push(spec);
    }
  }
  specs.push(buildQuickActionsSpec(user));
  return specs;
};
